import { nowUTC } from "@homebound/activesupport";
import {
  BoundMultiSelectField,
  BoundSelectField,
  BoundTextAreaField,
  BoundTextField,
  Button,
  ButtonMenu,
  Css,
  HasIdAndName,
  Icon,
  Palette,
  SelectField,
  useComputed,
  useModal,
} from "@homebound/beam";
import omit from "lodash/omit";
import { useCallback, useMemo } from "react";
import { Virtuoso } from "react-virtuoso";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import { MaterialType, PotentialOperation2 } from "src/generated/graphqlTypes";
import { useReaction } from "src/hooks";
import { useMaterialTypes } from "src/hooks/enums/useMaterialTypes";
import { BoundMaterialLeadTimeConfigFields } from "src/routes/admin/components/BoundMaterialLeadTimeConfigFields";
import { BoundMaterialLeadTimeConfigVariantsField } from "src/routes/admin/components/BoundMaterialLeadTimeConfigVariantsField";
import { BoundOptionPricingConfigFields } from "src/routes/admin/components/BoundOptionPricingConfigFields";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  MaterialListingFormState,
  MaterialVariantFormValue,
} from "src/routes/libraries/material-catalog/components/material-super-drawer/MaterialSuperDrawerContext";
import type {
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialBrandFragment,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import {
  MaterialCatalogMetadataDocument,
  useSaveMaterialBrandMutation,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { ObjectState } from "src/utils/formState";
import { MaterialAssemblyForm } from "../material-assembly-form/MaterialAssemblyForm";
import { useSaveMaterialListingMutation } from "../material-super-drawer/MaterialSuperDrawer.generated";
import { MaterialDimensionsForm } from "../MaterialDimensionsForm";
import { MaterialImageEditor } from "../MaterialImageEditor";
import { TakeoffsUsageModal } from "../takeoffs-usage-modal/TakeoffsUsageModal";

export type MaterialListingFormProps = {
  isNew: boolean;
  formState: MaterialListingFormState;
  canEditItem: PotentialOperation2 | undefined;
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  tags: NamedFragment[];
  isOptionCatalogV2Enabled?: boolean;
  markets: HasIdAndName[];
};

export function MaterialListingForm(props: MaterialListingFormProps) {
  const { isNew, formState, canEditItem, items, brands, tags, isOptionCatalogV2Enabled, markets } = props;
  const [saveMaterialBrand] = useSaveMaterialBrandMutation();
  const [saveMaterialListing] = useSaveMaterialListingMutation();
  const types = useMaterialTypes();
  const { openModal } = useModal();

  const type = useComputed(() => formState.type.value, [formState]);
  const brand = useComputed(() => formState.brandId.value, [formState]);
  const item = useComputed(() => items.find((i) => i.id === formState.itemId.value), [formState, items]);
  const variants = useComputed(() => formState.variants.rows, [formState]);

  const variantsErrors: Record<string, string> = useComputed(() => {
    const rows = formState.variants.rows;
    // Group variants by their MAV IDs + SKU
    const groups = rows.groupBy((variant) => {
      const mavKey = (variant.mavIds.value ?? []).sort().join(",");
      return `${variant.modelNumber.value}-${mavKey}`;
    });
    // Return duplicates
    return Object.fromEntries(
      groups
        .toEntries()
        .filter(([, dups]) => dups.length > 1)
        .flatMap(([, dups]) =>
          dups.map((variant) => {
            const i = rows.indexOf(variant);
            const other = dups.find((v) => v !== variant);
            const j = other ? rows.indexOf(other) : -1;
            return j !== -1 ? [i, `This is the same as “Variant ${j + 1}”`] : [];
          }),
        ),
    );
  }, [formState]);

  // Locks name to the item's name for Placeholder materials (and it's ignored by the backend)
  useReaction(
    () => [formState.type.value, formState.itemId.value],
    ([type, itemId]) => {
      if (type === MaterialType.Placeholder) {
        const name = items.find((i) => i.id === itemId)?.name;
        formState.name.set(name);
      }
    },
    [formState],
  );

  // Clear any unsaved components for Product  materials
  useReaction(
    () => [formState.variants, formState.type.value] as const,
    ([variants, type]) => {
      if (!canHaveComponents(type)) {
        variants.rows.forEach((mv) => mv.components.revertChanges());
      }
    },
    [formState.variants, formState.type.value],
  );

  async function saveBrand(newBrand: string | undefined) {
    if (newBrand) {
      const { data } = await saveMaterialBrand({
        variables: { input: { name: newBrand } },
        refetchQueries: [MaterialCatalogMetadataDocument],
      });
      if (data) {
        formState.brandId.set(data.saveMaterialBrand.materialBrand.id);
      }
    }
  }

  const onDelete = useCallback(async () => {
    // we can only delete a "config" if it actually exists
    if (formState.materialLeadTimeConfig.id.value) {
      await saveMaterialListing({
        variables: {
          input: {
            id: formState.id.value,
            itemId: formState.itemId.value,
            materialLeadTimeConfig: { delete: true, id: formState.materialLeadTimeConfig.id.value },
          },
        },
      });
    }
  }, [formState]);

  return (
    // Use negative margin here to account for the superdrawer Body padding
    <div css={Css.df.fdc.gap2.bgWhite.br8.add("margin", "-24px").$}>
      <Virtuoso
        // We need an exact height here the subtraction is for the superdrawer or full page header height
        style={{ height: isNew ? "calc(100vh - 260px)" : "calc(100vh - 90px)" }}
        // We are adding 2 sections to the list to where the scroll will be for the whole superdrawer body
        totalCount={variants.length + 2}
        itemContent={(index) => {
          if (index === 0) {
            return (
              <div css={Css.w75.df.fdc.gap2.m3.$}>
                {isNew && (
                  <div css={Css.w100.df.gap2.$}>
                    <div css={Css.wPx(150).$}>
                      <BoundSelectField field={formState.type} options={types} label="Type" />
                    </div>
                    <BoundTextField field={formState.name} label="Name" fullWidth />
                  </div>
                )}
                <div css={Css.df.gap2.$}>
                  <BoundSelectField
                    label="Item"
                    field={formState.itemId}
                    options={items}
                    disabled={
                      !canEditItem?.allowed ? canEditItem?.disabledReasons.map((r) => r.message).join(" ") : undefined
                    }
                  />
                  {/* Placeholders  can't have brand or website */}
                  {type !== MaterialType.Placeholder && (
                    <SelectField
                      label="Brand"
                      options={brands}
                      value={brand}
                      onSelect={(brandId) => formState.brandId.set(brandId)}
                      getOptionLabel={(brand) => brand.name}
                      getOptionValue={(brand) => brand.id}
                      onAddNew={saveBrand}
                    />
                  )}
                </div>
                <div css={Css.df.gap2.$}>
                  <BoundTextAreaField label="Description" field={formState.description} />
                </div>
                {isOptionCatalogV2Enabled && (
                  <>
                    <span css={Css.md.$}>Pricing</span>
                    <div css={Css.df.jcsb.pb1.$}>
                      <BoundOptionPricingConfigFields
                        postCutoffDesiredMarginInBasisPoints={
                          formState.optionPricingConfig.postCutoffDesiredMarginInBasisPoints
                        }
                        preCutoffDesiredMarginInBasisPoints={
                          formState.optionPricingConfig.preCutoffDesiredMarginInBasisPoints
                        }
                        selectionFeeInCents={formState.optionPricingConfig.selectionFeeInCents}
                        minimumPriceInCents={formState.optionPricingConfig.minimumPriceInCents}
                        reasonNote={formState.optionPricingConfig.reasonNote}
                      />
                    </div>
                    <span css={Css.md.$}>Material Lead Times</span>
                    <div css={Css.pb1.$}>
                      {/* Fields for MaterialLeadTimeConfig */}
                      <BoundMaterialLeadTimeConfigFields
                        expectedLeadTimeInDays={formState.materialLeadTimeConfig.expectedLeadTimeInDays}
                        maxLeadTimeInDays={formState.materialLeadTimeConfig.maxLeadTimeInDays}
                        minLeadTimeInDays={formState.materialLeadTimeConfig.minLeadTimeInDays}
                        reasonNote={formState.materialLeadTimeConfig.reasonNote}
                        deleteConfig={{ canDelete: !!formState.materialLeadTimeConfig.id.value, onDelete }}
                      />
                      {/* Fields for MaterialLeadTimeConfigVariant */}
                      <BoundMaterialLeadTimeConfigVariantsField
                        markets={markets}
                        materialLeadTimeConfig={formState.materialLeadTimeConfig}
                        isReadonly={false}
                      />
                    </div>
                  </>
                )}
              </div>
            );
          }
          if (index === variants.length + 1) {
            return (
              <div css={Css.p3.$}>
                <Button label="Add Variant" variant="secondary" onClick={() => formState.variants.add({})} />
              </div>
            );
          }
          const i = index - 1;
          const variantId = variants[i].value.id;
          return (
            <div css={Css.p3.$}>
              <MaterialVariantForm
                key={variants[i].id.value ?? i}
                number={i + 1}
                menuOpts={{
                  onCopy: () => formState.variants.add(omit(variants[i].value, "id", "code", "images")),
                  onDelete: variantId ? undefined : () => formState.variants.remove(variants[i].value),
                  openTakeoffUsage: variantId
                    ? () => openModal({ content: <TakeoffsUsageModal materialVariantId={variantId} /> })
                    : undefined,
                }}
                type={type}
                tags={tags}
                variant={variants[i]}
                item={item}
                error={variantsErrors[i]}
              />
            </div>
          );
        }}
      />
    </div>
  );
}

type MaterialVariantFormProps = {
  number: number;
  type: MaterialType | null | undefined;
  variant: ObjectState<MaterialVariantFormValue>;
  item: MaterialCatalog_ItemFragment | undefined;
  tags: NamedFragment[];
  error: string | undefined;
  menuOpts: {
    onCopy?: ((variant: ObjectState<MaterialVariantFormValue>) => void) | undefined;
    onDelete?: VoidFunction | undefined;
    openTakeoffUsage?: VoidFunction | undefined;
  };
};

function MaterialVariantForm({ number, type, menuOpts, variant, item, error, tags }: MaterialVariantFormProps) {
  const deletedAt = useComputed(() => variant.deletedAt.value, [variant]);
  const menuItems = useMemo(() => {
    return [
      ...(menuOpts.onCopy ? [{ label: "Make a copy", onClick: () => menuOpts.onCopy!(variant) }] : []),
      ...(menuOpts.onDelete ? [{ label: "Delete", onClick: menuOpts.onDelete }] : []),
      ...(menuOpts.openTakeoffUsage ? [{ label: "Takeoffs Usage", onClick: menuOpts.openTakeoffUsage }] : []),
    ];
  }, [menuOpts, variant]);

  const website = useComputed(() => {
    const url = variant.manufacturerUrl.value;
    if (!url) return undefined;
    return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
  }, [variant]);

  return (
    <div css={Css.df.fdc.gap1.$}>
      <div css={Css.df.jcsb.pb1.bb.bcGray400.$}>
        <div css={Css.df.gap2.$}>
          <span css={Css.md.$}>Variant {number}</span>
          <span css={Css.md.gray600.$}>{variant.code.value}</span>
        </div>
        <ButtonMenu trigger={{ icon: "verticalDots" }} items={menuItems} />
      </div>
      <div css={Css.df.gap2.mt2.$}>
        <MaterialImageEditor variant={variant} />
        <div css={Css.df.fg1.fdc.gap2.$}>
          {error && (
            <span css={Css.red600.$} data-testid="variantError">
              {error} please <b>modify</b> or{" "}
              <span css={Css.tdu.cursorPointer.$} onClick={menuOpts.onDelete}>
                delete
              </span>
            </span>
          )}
          <div css={Css.df.fg1.gap2.$}>
            {/* Placeholders  can't have model number */}
            {type !== MaterialType.Placeholder && (
              <>
                <div css={Css.wPx(160).$}>
                  <BoundTextField
                    field={variant.modelNumber}
                    label="SKU / Model"
                    disabled={variant.canEdit.value && disableBasedOnPotentialOperation(variant.canEdit.value)}
                  />
                </div>
                <div css={Css.wPx(160).$}>
                  <BoundTextField
                    label="Price"
                    field={variant.priceEstimate}
                    disabled="This is a price estimate based on market data and cost entries. Update it via the price link."
                  />
                </div>
              </>
            )}
            <div css={Css.wPx(120).$}>
              <SelectField
                value={deletedAt ? "archive" : "active"}
                options={[
                  { id: "active", name: "Active" },
                  { id: "archive", name: "Archive" },
                ]}
                onSelect={(status) => variant.deletedAt.set(status === "archive" ? nowUTC() : null)}
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o.id}
                label="Status"
              />
            </div>
            <BoundMultiSelectField
              options={tags}
              field={variant.tags}
              label="Tags"
              getOptionLabel={(o) => o.name}
              getOptionValue={(o) => o.id}
            />
          </div>
          <div css={Css.df.gap2.$}>
            <BoundTextField
              endAdornment={
                website && (
                  <a href={website} target="_blank" rel="noreferrer">
                    <Icon icon="linkExternal" color={Palette.Gray700} />
                  </a>
                )
              }
              field={variant.manufacturerUrl}
              label="Website"
            />
          </div>
          <div css={Css.w75.$}>
            <MaterialDimensionsForm
              type={type}
              variant={variant}
              dimensions={item?.materialAttributeDimensions ?? []}
            />
          </div>
          {canHaveComponents(type) && <MaterialAssemblyForm variant={variant} />}
        </div>
      </div>
    </div>
  );
}

function canHaveComponents(type: MaterialType | null | undefined): boolean {
  return type === MaterialType.Placeholder || type === MaterialType.Construction;
}
